import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { perfilFormSchema, PerfilFormData } from "@/pages/configuracoes/schemas";
import { usePerfil, useUpdatePerfil, useUploadProfileImage } from "@/services/queries/useConfiguracoes";
import { SectionDivider } from "./section-divider";
import { ProfileImageUpload } from "./profile-image-upload";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { SettingsFooterActions } from "./settings-footer-actions";

interface PerfilTabProps {
  onCancel: () => void;
}

export function PerfilTab({ onCancel }: PerfilTabProps) {
  const { data: perfilData, isLoading } = usePerfil();

  const updatePerfilMutation = useUpdatePerfil();
  const uploadProfileImageMutation = useUploadProfileImage();

  const form = useForm<PerfilFormData>({
    resolver: zodResolver(perfilFormSchema),
    defaultValues: {
      username: "",
      bio: "",
      profileImage: undefined,
      facebook: "",
      twitter: "",
      linkedin: "",
      instagram: "",
    },
  });

  // Populate form when data is fetched
  useEffect(() => {
    if (perfilData) {
      form.setValue("username", perfilData?.completeName ?? "");
      form.setValue("bio", perfilData?.biography || "");
      form.setValue("facebook", perfilData?.url_facebook || "");
      form.setValue("twitter", perfilData?.url_x || "");
      form.setValue("linkedin", perfilData?.url_linkedin || "");
      form.setValue("instagram", perfilData?.url_instagram || "");
    }
  }, [perfilData, form]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-[60px]">
        <p className="font-['Plus_Jakarta_Sans'] font-normal text-[16px] text-[#888888] leading-normal">
          Carregando...
        </p>
      </div>
    );
  }

  const handleSubmit = (data: PerfilFormData) => {
    updatePerfilMutation.mutate(data);
    if (data.profileImage instanceof File) {
      uploadProfileImageMutation.mutate(data.profileImage);
    }
  };

  return (
    <form onSubmit={form.handleSubmit(handleSubmit)} className="flex flex-col gap-4 w-full">
      <div className="bg-[#15203c] rounded-lg p-4 flex flex-col gap-5">
        {/* Title and Description */}
        <div className="flex flex-col gap-0.5 leading-normal">
          <h2 className="font-['Plus_Jakarta_Sans'] font-bold text-xl text-white">
            Perfil
          </h2>
          <p className="font-['Plus_Jakarta_Sans'] font-normal text-xs text-[#d1d1d1]">
            Atualize seu perfil e seus dados
          </p>
        </div>

        {/* Profile Section */}
        <div className="flex flex-col gap-3">
          {/* Section Divider */}
          <SectionDivider title="PERFIL DO USUÁRIO" />

          {/* Username and Bio Row */}
          <div className="flex gap-4 items-center w-full">
            {/* Username Input */}
            <div className="flex flex-col gap-2 w-[374px]">
              <label className="font-['Plus_Jakarta_Sans'] font-bold text-sm text-white leading-normal">
                Seu nome de Usuário
              </label>
              <Input
                placeholder="Digite o nome"
                {...form.register("username")}
              />
              {form.formState.errors.username && (
                <p className="text-[#dc4426] text-xs">
                  {form.formState.errors.username.message}
                </p>
              )}
            </div>

            {/* Bio Input */}
            <div className="flex flex-col gap-2 w-[964px]">
              <label className="font-['Plus_Jakarta_Sans'] font-bold text-sm text-white leading-normal">
                Descrição / Biografia
              </label>
              <Textarea
                placeholder="Digite sua biografia"
                className="min-h-input-default"
                {...form.register("bio")}
              />
              {form.formState.errors.bio && (
                <p className="text-[#dc4426] text-xs">
                  {form.formState.errors.bio.message}
                </p>
              )}
            </div>
          </div>

          {/* Profile Image and Social Links Row */}
          <div className="flex gap-[30px] items-start w-full">
            {/* Left Column: Profile Image */}
            <div className="flex flex-col gap-[18px] w-[535px]">
              <label className="font-['Plus_Jakarta_Sans'] font-bold text-[16px] text-white leading-normal">
                Imagem de Perfil
              </label>
              <div className="bg-[#15203c] border border-dashed border-[#5d5d5d] rounded-[10px] px-[49px] py-[10px] flex gap-[49px] items-center h-[186px]">
                <ProfileImageUpload
                  value={form.watch("profileImage") || perfilData?.url_avatar || null}
                  onChange={(file) => form.setValue("profileImage", file)}
                />
                <div className="flex flex-col gap-[7px] w-[196px]">
                  <p className="font-['Plus_Jakarta_Sans'] font-bold text-[14px] text-[#f6f6f6] leading-normal">
                    Alterar foto
                  </p>
                  <p className="font-['Plus_Jakarta_Sans'] font-normal text-[14px] text-[#d1d1d1] leading-normal whitespace-nowrap">
                    Clique para enviar seu arquivo
                  </p>
                </div>
              </div>
            </div>

            {/* Right Column: Social Links (2x2 grid) */}
            <div className="flex flex-col gap-[30px] flex-1">
              {/* First Row: Facebook and X */}
              <div className="flex gap-[29px]">
                {/* Facebook */}
                <div className="flex flex-col gap-[18px] flex-1">
                  <label className="font-['Plus_Jakarta_Sans'] font-bold text-[16px] text-white leading-normal">
                    Facebook
                  </label>
                  <Input
                    placeholder="https://www.facebook.com/"
                    {...form.register("facebook")}
                  />
                  {form.formState.errors.facebook && (
                    <p className="text-[#dc4426] text-[14px]">
                      {form.formState.errors.facebook.message}
                    </p>
                  )}
                </div>

                {/* X (Twitter) */}
                <div className="flex flex-col gap-[18px] w-[438.5px]">
                  <label className="font-['Plus_Jakarta_Sans'] font-bold text-[16px] text-white leading-normal">
                    X
                  </label>
                  <Input
                    placeholder="https://x.com/"
                    {...form.register("twitter")}
                  />
                  {form.formState.errors.twitter && (
                    <p className="text-[#dc4426] text-[14px]">
                      {form.formState.errors.twitter.message}
                    </p>
                  )}
                </div>
              </div>

              {/* Second Row: LinkedIn and Instagram */}
              <div className="flex gap-[29px]">
                {/* LinkedIn */}
                <div className="flex flex-col gap-[18px] w-[460px]">
                  <label className="font-['Plus_Jakarta_Sans'] font-bold text-[16px] text-white leading-normal">
                    Linkedin
                  </label>
                  <Input
                    placeholder="https://www.linkedin.com/"
                    {...form.register("linkedin")}
                  />
                  {form.formState.errors.linkedin && (
                    <p className="text-[#dc4426] text-[14px]">
                      {form.formState.errors.linkedin.message}
                    </p>
                  )}
                </div>

                {/* Instagram */}
                <div className="flex flex-col gap-[18px] flex-1">
                  <label className="font-['Plus_Jakarta_Sans'] font-bold text-[16px] text-white leading-normal">
                    Instagram
                  </label>
                  <Input
                    placeholder="@usuario"
                    {...form.register("instagram")}
                  />
                  {form.formState.errors.instagram && (
                    <p className="text-[#dc4426] text-[14px]">
                      {form.formState.errors.instagram.message}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        <button type="submit" id="perfil-submit" className="hidden" />
        <SettingsFooterActions
          onSave={() => document.getElementById("perfil-submit")?.click()}
          onCancel={onCancel}
          isSaving={updatePerfilMutation.isPending || uploadProfileImageMutation.isPending}
        />
      </div>
    </form>
  );
}

PerfilTab.displayName = "PerfilTab";
